import React from "react";
import { useParams, Link } from "react-router-dom";
import { products } from "../mock";
import { ArrowLeft, CheckCircle2, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import NotFound from "./NotFound";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = products.find(p => p.slug === slug);

  if (!product) return <NotFound />;

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };

  return (
    <div className="w-full bg-white min-h-screen font-sans text-black">
      {/* HERO SECTION */}
      <div className="relative pt-40 pb-32 px-6 lg:px-12 bg-black text-white">
        <div className="absolute inset-0 z-0">
          <img src={product.image} alt={product.title} className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
        </div>
        
        <div className="container mx-auto max-w-6xl relative z-10 flex flex-col justify-end h-full min-h-[30vh]">
          <Link to="/prodotti" className="inline-flex items-center gap-2 text-gray-300 hover:text-white mb-8 transition-colors uppercase tracking-widest text-xs font-bold w-fit">
            <ArrowLeft size={16} /> Torna a Prodotti
          </Link>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl font-bold tracking-tighter mb-6 leading-tight"
          >
            {product.title}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.3 }}
            className="text-xl md:text-2xl text-gray-300 font-light max-w-3xl leading-relaxed"
          >
            {product.description}
          </motion.p>
        </div>
      </div>

      {/* CONTENT SECTION */}
      <div className="container mx-auto max-w-6xl px-6 lg:px-12 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-16 lg:gap-24">
          
          {/* LEFT COL: Description & Features */}
          <motion.div 
            initial="hidden" animate="visible" variants={staggerContainer}
            className="md:col-span-7 space-y-16"
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tighter mb-8 text-[#7dba00]">L'Eccellenza del Sistema</h2>
              <div className="text-lg md:text-xl text-gray-600 font-light leading-relaxed space-y-6">
                <p>{product.details.longDescription}</p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <h3 className="text-2xl md:text-3xl font-bold tracking-tighter mb-8">Punti di Forza</h3>
              <ul className="space-y-4">
                {product.details.features.map((feat) => (
                  <li key={feat} className="flex items-start gap-4 text-lg text-gray-700 font-light">
                    <CheckCircle2 className="text-[#7dba00] shrink-0 mt-1" size={20} />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </motion.div>

          {/* RIGHT COL: Specs & CTA */}
          <motion.div 
            initial="hidden" animate="visible" variants={fadeInUp}
            className="md:col-span-5"
          >
            <div className="bg-gray-50 p-8 md:p-12 border border-gray-100 sticky top-32">
              <h3 className="text-2xl font-bold tracking-tighter mb-8 uppercase text-black">Specifiche Tecniche</h3>
              <div className="space-y-6 mb-12">
                {product.details.specs.map((spec) => (
                  <div key={spec.label} className="border-b border-gray-200 pb-4">
                    <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-1">{spec.label}</div>
                    <div className="text-lg text-black font-medium">{spec.value}</div>
                  </div>
                ))}
              </div>
              
              <div className="pt-4 border-t border-gray-300">
                <p className="text-sm text-gray-500 mb-6 font-light">
                  Interessato a questa soluzione per il tuo progetto?
                </p>
                <Link to="/contatti" className="flex items-center justify-center gap-3 bg-black text-white px-8 py-5 uppercase text-sm font-bold tracking-widest hover:bg-[#7dba00] transition-colors w-full group">
                  Richiedi Preventivo <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </motion.div>

        </div>
      </div>

    </div>
  );
}